import { Group, Stack, Text } from '@mantine/core'
import {
  IconAlertCircle, IconCircleCheck, IconDatabaseOff, IconLock, IconMinus, IconPlugConnectedX, IconPoint,
} from '@tabler/icons-react'
import { EmptyState, SeverityBadge } from '@lighthouse/design'
import { severityLabel, severityLevel, signalErrorView, signalState } from './signalFormat'
import type { EmptyView, SignalErrorView } from './signalFormat'
import { handlingChip, handlingView } from './signalHandlingFormat'
import type { HandlingSubject } from './signalHandlingFormat'
import type { Signal } from '../../types/signals'

/*
 * Small pieces the Signal Inbox, its drawer and the Overview preview share. Components only (react-refresh);
 * every decision they draw is made in signalFormat.ts.
 */

/** A signal's severity on the design system's SEVERITY scale — deliberately not the status scale. */
export function SignalSeverity({ severity }: { severity: string }) {
  return <SeverityBadge severity={severityLevel(severity)} label={severityLabel(severity)} />
}

/**
 * A signal's state. Only `cleared` carries the check mark and the good colour; every other ending — no longer
 * reported, acknowledged in Data Comb, no longer tracked, out of scope — is a neutral dash, because none of them
 * proves the condition went away.
 */
export function SignalStateLabel({ signal }: {
  signal: Pick<Signal, 'isOpen' | 'observation' | 'unobservedSince' | 'resolution' | 'subjectKind'>
}) {
  const state = signalState(signal)
  const icon = state.tone === 'cleared'
    ? <span style={{ color: 'var(--status-good)', display: 'inline-flex' }}><IconCircleCheck size={14} /></span>
    : state.tone === 'open'
      ? <span style={{ color: 'var(--status-warning-fg)', display: 'inline-flex' }}><IconPoint size={14} /></span>
      : <span style={{ color: 'var(--color-text-disabled)', display: 'inline-flex' }}><IconMinus size={14} /></span>
  return (
    <Group gap={4} wrap="nowrap" align="center" data-signal-state={state.tone}>
      {icon}
      <Text size="sm" c={state.tone === 'unconfirmed' || state.tone === 'ended' ? 'dimmed' : undefined}>{state.text}</Text>
    </Group>
  )
}

function errorIcon(view: SignalErrorView, size: number) {
  switch (view.kind) {
    case 'network': return <IconPlugConnectedX size={size} stroke={1.5} />
    case 'forbidden': return <IconLock size={size} stroke={1.5} />
    case 'unprovisioned':
    case 'handling-unprovisioned': return <IconDatabaseOff size={size} stroke={1.5} />
    default: return <IconAlertCircle size={size} stroke={1.5} />
  }
}

/** A failed signal request, worded by its status (signalErrorView). `compact` is the Overview preview's form. */
export function SignalErrorState({ error, compact = false }: { error: unknown; compact?: boolean }) {
  const view = signalErrorView(error)
  if (compact) {
    return (
      <Group gap={6} wrap="nowrap" align="flex-start" role="status" data-signal-error={view.kind}>
        <span style={{ flexShrink: 0, marginTop: 2, color: 'var(--color-text-disabled)' }}>{errorIcon(view, 14)}</span>
        <Text size="xs" c="dimmed"><strong>{view.title}.</strong> {view.message}</Text>
      </Group>
    )
  }
  return (
    <div role="status" data-signal-error={view.kind}>
      <EmptyState icon={errorIcon(view, 28)} title={view.title} message={view.message} />
    </div>
  )
}

/** An empty list. The check mark appears ONLY for a Complete store with nothing open (emptyView.allClear). */
export function SignalEmptyState({ view, compact = false }: { view: EmptyView; compact?: boolean }) {
  const icon = view.allClear
    ? <IconCircleCheck size={compact ? 14 : 28} stroke={1.5} style={{ color: 'var(--status-good)' }} />
    : <IconMinus size={compact ? 14 : 28} stroke={1.5} style={{ color: 'var(--color-text-disabled)' }} />
  if (compact) {
    return (
      <Group gap={6} wrap="nowrap" align="flex-start" data-signal-empty={view.allClear ? 'all-clear' : 'unproven'}>
        <span style={{ flexShrink: 0, marginTop: 2 }}>{icon}</span>
        <Text size="xs" c="dimmed">
          <strong>{view.title}</strong>{view.message && <> — {view.message}</>}
        </Text>
      </Group>
    )
  }
  return (
    <div data-signal-empty={view.allClear ? 'all-clear' : 'unproven'}>
      <EmptyState icon={icon} title={view.title} message={view.message ?? undefined} />
    </div>
  )
}

/**
 * Who has a signal, short — the preview row's chip. "Unowned" is plain text (a measured state); a missing handling
 * table is a dimmed dash whose tooltip says why; a changed-since one is in the warning colour. Decided by
 * signalHandlingFormat.handlingChip.
 */
export function SignalHandlingChip({ signal }: { signal: HandlingSubject }) {
  const chip = handlingChip(signal)
  return (
    <Text
      size="xs"
      title={chip.title}
      c={chip.tone === 'unmeasured' ? 'dimmed' : undefined}
      style={chip.tone === 'changed' ? { color: 'var(--status-warning-fg)' } : undefined}
      data-signal-handling={chip.tone === 'unmeasured' ? 'unrecorded' : handlingView(signal).kind}
    >
      {chip.text}
    </Text>
  )
}

/**
 * Who has a signal, in full — the table's Handling cell: the state line, who did the latest act, a withheld name,
 * and the changed-since warning, each on its own line.
 */
export function SignalHandlingCell({ signal }: { signal: HandlingSubject }) {
  const view = handlingView(signal)
  return (
    <Stack gap={0} data-signal-handling={view.kind}>
      <Text size="sm" c={view.kind === 'unrecorded' ? 'dimmed' : undefined}>{view.text}</Text>
      {view.note && <Text size="xs" c="dimmed">{view.note}</Text>}
      {view.byline && <Text size="xs" c="dimmed">{view.byline}</Text>}
      {view.withheld && <Text size="xs" c="dimmed" data-state="names-withheld">{view.withheld}</Text>}
      {view.changed && (
        <Text size="xs" style={{ color: 'var(--status-warning-fg)' }} data-signal-handling-changed="">{view.changed}</Text>
      )}
    </Stack>
  )
}
